import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { 
  Sheet, 
  SheetContent, 
  SheetHeader, 
  SheetTitle,
  SheetFooter
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Copy, 
  ExternalLink, 
  History, 
  AlertTriangle,
  Mail,
  Phone,
  Building,
  Calendar,
  Clock,
  Heart,
  Eye,
  MessageSquare,
  Search,
  Trash2,
  RefreshCw,
  UserX,
  Key,
  ShieldAlert,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  EyeOff
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { registrarLogAdmin } from "@/utils/admin-logs";
import { useAuth } from "@/hooks/use-auth";
import { adminUpdatePassword } from "@/lib/admin.functions";

interface UserDetailsSheetProps {
  userId: string | null;
  userInitialData?: any;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UserDetailsSheet({ userId, userInitialData, isOpen, onOpenChange }: UserDetailsSheetProps) {
  const { profile: adminProfile } = useAuth();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<any>({
    nome: "",
    telefone: "",
    empresa: "",
    email_gopage_alternativo: "",
    status: "pendente",
    role: "user",
    segmentos_interesse: []
  });
  const [motivo, setMotivo] = useState("");
  const [isManualPasswordOpen, setIsManualPasswordOpen] = useState(false);
  const [showNovaSenha, setShowNovaSenha] = useState(false);
  const [novaSenha, setNovaSenha] = useState("");
  const [confirmSenha, setConfirmSenha] = useState("");
  const runAdminUpdatePassword = useServerFn(adminUpdatePassword);

  const { data: user, isLoading, isError } = useQuery({
    queryKey: ["admin-user-detail", userId],
    queryFn: async () => {
      if (!userId) return null;
      
      // Get profile
      const { data: profile, error: pError } = await supabase
        .from("profiles")
        .select("*, aprovado_por_rel:profiles!profiles_aprovado_por_fkey(nome)")
        .eq("id", userId)
        .maybeSingle();
      
      if (pError) throw pError;
      if (!profile) return null;

      // Get additional stats using COALESCE patterns via RPC or safer aggregation
      // For now we do robust manual counting to ensure 0 vs null
      const [favoritosRes, searchTermsRes, statsRes, empresaRes] = await Promise.all([
        supabase.from("favoritos").select("*", { count: "exact", head: true }).eq("user_id", userId),
        supabase.from("eventos").select("termo, created_at").eq("user_id", userId).eq("tipo", "busca").order("created_at", { ascending: false }).limit(10),
        supabase.from("eventos").select("tipo").eq("user_id", userId).or("tipo.eq.visualizacao_perfil,tipo.eq.clique_whatsapp").gte("created_at", new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()),
        supabase.from("fornecedores").select("id, nome, status").eq("owner_id", userId).maybeSingle(),
      ]).catch(err => {
        console.error("Erro ao carregar atividade/estatísticas:", err);
        return [ { count: 0 }, { data: [] }, { data: [] }, { data: null } ];
      }) as any;

      const aprovadorData = profile.aprovado_por_rel as any;
      const aprovadorNome = Array.isArray(aprovadorData) ? aprovadorData[0]?.nome : aprovadorData?.nome;
      const segmentosInteresse = Array.isArray(profile.segmentos_interesse) ? profile.segmentos_interesse : [];

      return {
        ...profile,
        segmentos_interesse: segmentosInteresse,
        aprovadorNome,
        stats: {
          favoritos: favoritosRes?.count ?? 0,
          buscas: searchTermsRes?.data ?? [],
          visualizacoes: (statsRes?.data ?? []).filter((e: any) => e.tipo === 'visualizacao_perfil').length,
          cliques: (statsRes?.data ?? []).filter((e: any) => e.tipo === 'clique_whatsapp').length
        },
        empresa: empresaRes?.data
      };
    },
    enabled: !!userId && isOpen
  });

  const { data: logs } = useQuery({
    queryKey: ["admin-user-logs", userId],
    queryFn: async () => {
      if (!userId) return [];
      const { data } = await (supabase.from("log_admin" as any) as any)
        .select("*, admin:profiles(nome)")
        .eq("registro_id", userId)
        .order("created_at", { ascending: false });
      return data || [];
    },
    enabled: !!userId && isOpen
  });

  const { data: segmentos = [] } = useQuery({
    queryKey: ['segmentos-active'],
    queryFn: async () => {
      const { data } = await supabase.from('segmentos').select('*').eq('ativo', true).order('ordem');
      return data || [];
    }
  });

  useEffect(() => {
    const dataToReset = user || userInitialData;
    if (dataToReset) {
      setFormData({
        nome: dataToReset.nome || "",
        telefone: dataToReset.telefone || "",
        empresa: dataToReset.empresa || "",
        email_gopage_alternativo: dataToReset.email_gopage_alternativo || "",
        status: dataToReset.status || "pendente",
        role: dataToReset.role || "user",
        segmentos_interesse: Array.isArray(dataToReset.segmentos_interesse) ? dataToReset.segmentos_interesse : []
      });
    }
  }, [user, userInitialData, isOpen]);

  const updateMutation = useMutation({
    mutationFn: async (newData: any) => {
      if (!userId || !adminProfile) return;

      const { data: current } = await supabase.from("profiles").select("*").eq("id", userId).single();
      
      const updatePayload: any = { ...newData };
      if (newData.status === 'aprovado' && current?.status !== 'aprovado') {
        updatePayload.aprovado_em = new Date().toISOString();
        updatePayload.aprovado_por = adminProfile.id;
      }

      const { error } = await supabase
        .from("profiles")
        .update(updatePayload)
        .eq("id", userId);

      if (error) throw error;

      await registrarLogAdmin({
        adminId: adminProfile.id,
        acao: "Edição de usuário",
        tabela: "profiles",
        registroId: userId,
        dadosAntes: current,
        dadosDepois: { ...current, ...updatePayload }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      queryClient.invalidateQueries({ queryKey: ["admin-user-detail", userId] });
      toast.success("Usuário atualizado com sucesso!");
    },
    onError: (err: any) => {
      toast.error("Erro ao atualizar: " + err.message);
    }
  });

  const handleDelete = async () => {
    if (!userId || !adminProfile || adminProfile.role !== 'super_admin') return;
    
    const confirmNome = window.prompt("Para excluir permanentemente, digite o NOME do usuário (" + user?.nome + "):");
    if (confirmNome !== user?.nome) {
      toast.error("Confirmação incorreta.");
      return;
    }

    const { error } = await supabase.from("profiles").delete().eq("id", userId);
    if (error) {
      toast.error("Erro ao excluir: " + error.message);
    } else {
      toast.success("Usuário excluído.");
      onOpenChange(false);
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    }
  };

  const statusColors: any = {
    pendente: "bg-amber-100 text-amber-700",
    aprovado: "bg-green-100 text-green-700",
    nao_cliente: "bg-blue-100 text-blue-700",
    bloqueado: "bg-red-100 text-red-700",
  };

  const handleResetPasswordEmail = async () => {
    if (!user?.email) return;
    const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
      redirectTo: `${window.location.origin}/redefinir-senha`,
    });
    if (error) {
      toast.error("Erro ao enviar e-mail: " + error.message);
    } else {
      toast.success("E-mail de redefinição enviado!");
      if (adminProfile) {
        await registrarLogAdmin({
          adminId: adminProfile.id,
          acao: "E-mail de redefinição de senha enviado",
          tabela: "profiles",
          registroId: userId!,
          dadosAntes: { email: user.email },
          dadosDepois: { status: "enviado" }
        });
      }
    }
  };

  const handleManualPasswordReset = async () => {
    if (novaSenha.length < 8) {
      toast.error("A senha deve ter pelo menos 8 caracteres.");
      return;
    }
    if (novaSenha !== confirmSenha) {
      toast.error("As senhas não conferem.");
      return;
    }

    try {
      await runAdminUpdatePassword({
        data: {
          userId: userId!,
          novaSenha
        }
      });
      toast.success("Senha alterada com sucesso!");
      setIsManualPasswordOpen(false);
      setNovaSenha("");
      setConfirmSenha("");
      
      if (adminProfile) {
        await registrarLogAdmin({
          adminId: adminProfile.id,
          acao: "Senha alterada manualmente pelo admin",
          tabela: "profiles",
          registroId: userId!,
          dadosAntes: {},
          dadosDepois: { acao: "password_reset_manual" }
        });
      }
    } catch (error: any) {
      toast.error("Erro ao alterar senha: " + error.message);
    }
  };

  const gerarSenhaAleatoria = () => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+";
    let password = "";
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNovaSenha(password);
    setConfirmSenha(password);
  };

  if (!userId) return null;

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[520px] p-0 flex flex-col">
        <SheetHeader className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <SheetTitle className="text-xl font-bold">
                {userInitialData?.nome || user?.nome || (isLoading && !userInitialData ? "Carregando..." : "Usuário")}
              </SheetTitle>
              <div className="flex items-center gap-2">
                {(userInitialData?.status || user?.status) && (
                  <Badge className={cn("capitalize shadow-none border-none", statusColors[userInitialData?.status || user?.status])}>
                    {(userInitialData?.status || user?.status).replace("_", " ")}
                  </Badge>
                )}
                {(userInitialData?.role !== 'user' || user?.role !== 'user') && (
                  <Badge variant="outline" className="capitalize text-[10px] py-0 border-amber-200 text-amber-700">
                    {(userInitialData?.role || user?.role) === 'super_admin' ? 'Super Admin' : 'Admin'}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1">
          <div className="p-6 space-y-8 pb-32">
            {/* SEÇÃO DADOS */}
            <section className="space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-wider text-gray-400">Dados do Usuário</h3>
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label>Nome Completo</Label>
                  <Input 
                    value={formData.nome} 
                    onChange={e => setFormData({...formData, nome: e.target.value})} 
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Telefone / WhatsApp</Label>
                  <Input 
                    value={formData.telefone} 
                    onChange={e => setFormData({...formData, telefone: e.target.value})} 
                  />
                </div>

                <div className="space-y-2">
                  <Label>Nome da Empresa</Label>
                  <Input 
                    value={formData.empresa} 
                    onChange={e => setFormData({...formData, empresa: e.target.value})} 
                  />
                </div>

                <div className="space-y-2">
                  <Label>E-mail Principal (Login)</Label>
                  <div className="flex gap-2">
                    <Input value={user?.email || ""} readOnly className="bg-gray-50 text-gray-500" />
                    <Button variant="outline" size="icon" onClick={() => {
                      navigator.clipboard.writeText(user?.email || "");
                      toast.success("E-mail copiado!");
                    }}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>E-mail Alternativo Gopage</Label>
                  <Input 
                    value={formData.email_gopage_alternativo} 
                    onChange={e => setFormData({...formData, email_gopage_alternativo: e.target.value})} 
                    placeholder="Informado se o principal não for cliente"
                  />
                </div>
                <div className="space-y-3">
                  <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Segmentos de interesse</Label>
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-4 bg-gray-50 border rounded-xl">
                    {segmentos.map((seg: any) => (
                      <div 
                        key={seg.id}
                        className="flex items-center space-x-2 p-2 rounded-lg bg-white border cursor-pointer hover:border-brand-primary/50 transition-colors"
                        onClick={() => {
                          const current = [...(formData.segmentos_interesse || [])];
                          const index = current.indexOf(seg.id);
                          if (index > -1) {
                            current.splice(index, 1);
                          } else {
                            current.push(seg.id);
                          }
                          setFormData({ ...formData, segmentos_interesse: current });
                        }}
                      >
                        <Checkbox 
                          checked={(formData.segmentos_interesse || []).includes(seg.id)}
                          onCheckedChange={() => {}} // Handled by div click
                        />
                        <span className="text-[11px] font-medium truncate">{seg.nome}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <Separator />

            {/* SEÇÃO ACESSO */}
            <section className="space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-wider text-gray-400">Controle de Acesso</h3>
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label>Status da Conta</Label>
                  <Select 
                    value={formData.status} 
                    onValueChange={v => setFormData({...formData, status: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pendente">Pendente</SelectItem>
                      <SelectItem value="aprovado">Aprovado</SelectItem>
                      <SelectItem value="nao_cliente">Não-cliente</SelectItem>
                      <SelectItem value="bloqueado">Bloqueado</SelectItem>
                    </SelectContent>
                  </Select>
                  {(formData.status === 'nao_cliente' || formData.status === 'bloqueado') && (
                    <div className="mt-2">
                      <Label className="text-xs text-red-600">Motivo (Obrigatório)</Label>
                      <Input 
                        placeholder="Ex: Não localizado na base de clientes" 
                        value={motivo} 
                        onChange={e => setMotivo(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  )}
                </div>

                {adminProfile?.role === 'super_admin' && (
                  <div className="space-y-2">
                    <Label>Papel (Role)</Label>
                    <Select 
                      value={formData.role} 
                      onValueChange={v => setFormData({...formData, role: v})}
                      disabled={user?.id === adminProfile.id}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">Usuário Comum</SelectItem>
                        <SelectItem value="admin">Administrador</SelectItem>
                        <SelectItem value="super_admin">Super Admin</SelectItem>
                      </SelectContent>
                    </Select>
                    {user?.id === adminProfile.id && (
                      <p className="text-[10px] text-gray-400 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> Você não pode alterar seu próprio papel.
                      </p>
                    )}
                  </div>
                )}

                {user?.aprovado_em && (
                  <div className="bg-gray-50 p-3 rounded-lg text-xs space-y-1">
                    <p className="text-gray-500">Aprovado por: <span className="font-medium text-gray-700">{user.aprovadorNome || "Sistema"}</span></p>
                    <p className="text-gray-500">Data: <span className="font-medium text-gray-700">{format(new Date(user.aprovado_em), "dd/MM/yyyy 'às' HH:mm")}</span></p>
                  </div>
                )}
              </div>
            </section>

            <Separator />

            {/* SEÇÃO ATIVIDADE */}
            <section className="space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-wider text-gray-400">Atividade</h3>
              
              {isLoading ? (
                <div className="space-y-4 animate-pulse">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="h-20 bg-gray-100 rounded-xl" />
                    <div className="h-20 bg-gray-100 rounded-xl" />
                  </div>
                  <div className="h-12 bg-gray-100 rounded-lg" />
                </div>
              ) : isError || !user ? (
                <div className="bg-red-50 p-4 rounded-xl text-center">
                  <p className="text-xs text-red-600">Erro ao carregar dados de atividade.</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white border rounded-xl p-4 flex flex-col items-center justify-center text-center">
                      <span className="text-2xl font-bold text-brand-dark">{user.stats?.favoritos ?? 0}</span>
                      <span className="text-[10px] text-gray-400 uppercase font-bold flex items-center gap-1">
                        <Heart className="w-3 h-3 text-red-400" /> Favoritos
                      </span>
                    </div>
                    <div className="bg-white border rounded-xl p-4 flex flex-col items-center justify-center text-center">
                      <span className="text-2xl font-bold text-brand-dark">{user.stats?.visualizacoes ?? 0}</span>
                      <span className="text-[10px] text-gray-400 uppercase font-bold flex items-center gap-1">
                        <Eye className="w-3 h-3 text-blue-400" /> Vistos (30d)
                      </span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500 flex items-center gap-2"><Calendar className="w-4 h-4" /> Cadastro</span>
                      <span className="font-medium">{user.created_at ? format(new Date(user.created_at), "dd/MM/yyyy") : "-"}</span>
                    </div>
                    
                    {user.empresa && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500 flex items-center gap-2"><Building className="w-4 h-4" /> Empresa</span>
                        <a href={"/fornecedores/" + user.empresa.id} target="_blank" className="text-brand-dark font-bold hover:underline flex items-center gap-1">
                          {user.empresa.nome} <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs">Últimas Buscas</Label>
                    <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                      {user.stats?.buscas && user.stats.buscas.length > 0 ? (
                        user.stats.buscas.map((b: any, i: number) => (
                          <div key={i} className="flex items-center justify-between text-xs border-b border-gray-100 last:border-0 pb-1 last:pb-0">
                            <span className="text-gray-700 italic">"{b.termo}"</span>
                            <span className="text-gray-400 text-[10px]">{format(new Date(b.created_at), "dd/MM HH:mm")}</span>
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-gray-400 text-center py-2 italic">Este usuário ainda não teve atividade registrada.</p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </section>

            <Separator />

            {/* SEÇÃO LOGS */}
            <section className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold uppercase tracking-wider text-gray-400">Histórico de Alterações</h3>
                <History className="w-4 h-4 text-gray-300" />
              </div>
              <div className="space-y-3">
                {logs?.length > 0 ? (
                  logs.map((log: any) => (
                    <div key={log.id} className="bg-white border rounded-lg p-3 text-xs space-y-1">
                      <div className="flex justify-between font-bold">
                        <span>{log.acao}</span>
                        <span className="text-[10px] text-gray-400">{format(new Date(log.created_at), "dd/MM/yy HH:mm")}</span>
                      </div>
                      <p className="text-gray-500">Por: {log.admin?.nome || "Admin"}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-gray-400 text-center italic">Nenhum log encontrado.</p>
                )}
              </div>
            </section>

            <Separator />

            {/* SEÇÃO SEGURANÇA */}
            {adminProfile?.role === 'super_admin' && (
              <section className="space-y-4">
                <h3 className="text-sm font-bold uppercase tracking-wider text-gray-400">Segurança</h3>
                <div className="grid gap-4">
                  <Button variant="outline" className="w-full justify-start text-sm h-12" onClick={handleResetPasswordEmail}>
                    <Mail className="w-4 h-4 mr-2" /> Enviar link de redefinição por e-mail
                  </Button>

                  <div className={cn("border rounded-xl overflow-hidden bg-gray-50/50", isManualPasswordOpen && "border-amber-200")}>
                    <button 
                      onClick={() => setIsManualPasswordOpen(!isManualPasswordOpen)}
                      className="w-full p-4 flex items-center justify-between text-sm font-medium hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center">
                        <Key className="w-4 h-4 mr-2 text-amber-600" /> 
                        Definir senha manualmente
                      </div>
                      {isManualPasswordOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    
                    {isManualPasswordOpen && (
                      <div className="p-4 pt-0 space-y-4">
                        <div className="bg-amber-50 p-3 rounded-lg border border-amber-100 flex gap-2">
                          <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                          <p className="text-[11px] text-amber-700 leading-relaxed">
                            Use apenas quando o usuário não conseguir receber o e-mail. 
                            A senha será alterada imediatamente no sistema.
                          </p>
                        </div>
                        
                        <div className="space-y-3">
                          <div className="space-y-1.5">
                            <Label className="text-xs">Nova Senha</Label>
                            <div className="flex gap-2">
                              <div className="relative flex-1">
                                <Input 
                                  type={showNovaSenha ? "text" : "password"}
                                  value={novaSenha}
                                  onChange={e => setNovaSenha(e.target.value)}
                                  placeholder="Mínimo 8 caracteres"
                                  className="h-10 text-sm pr-10"
                                />
                                <button 
                                  type="button"
                                  onClick={() => setShowNovaSenha(!showNovaSenha)}
                                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                {showNovaSenha ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                              </div>
                              <Button variant="outline" size="icon" onClick={gerarSenhaAleatoria} title="Gerar senha segura">
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                              <Button variant="outline" size="icon" onClick={() => {
                                navigator.clipboard.writeText(novaSenha);
                                toast.success("Senha copiada!");
                              }}>
                                <Copy className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-xs">Confirmar Senha</Label>
                            <Input 
                              type={showNovaSenha ? "text" : "password"}
                              value={confirmSenha}
                              onChange={e => setConfirmSenha(e.target.value)}
                              placeholder="Repita a nova senha"
                              className="h-10 text-sm"
                            />
                          </div>

                          <Button 
                            className="w-full bg-brand-dark text-white font-bold h-10 mt-2" 
                            onClick={handleManualPasswordReset}
                          >
                            Salvar Nova Senha
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  <Button variant="outline" className="w-full justify-start text-sm h-12 text-red-600 hover:text-red-700" onClick={() => toast.info("Funcionalidade em desenvolvimento")}>
                    <UserX className="w-4 h-4 mr-2" /> Encerrar todas as sessões deste usuário
                  </Button>
                </div>
              </section>
            )}

            <Separator />

            {/* SEÇÃO AÇÕES */}
            <section className="space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-wider text-gray-400">Ações Críticas</h3>
              <div className="grid gap-2">
                <Button variant="outline" className="w-full justify-start text-sm" onClick={() => toast.info("Funcionalidade em desenvolvimento")}>
                  <Mail className="w-4 h-4 mr-2" /> Reenviar e-mail de aprovação
                </Button>
                {adminProfile?.role === 'super_admin' && (
                  <Button variant="destructive" className="w-full justify-start text-sm" onClick={handleDelete} disabled={user?.id === adminProfile.id}>
                    <Trash2 className="w-4 h-4 mr-2" /> Excluir usuário permanentemente
                  </Button>
                )}
              </div>
            </section>
          </div>
        </ScrollArea>

        <SheetFooter className="absolute bottom-0 left-0 right-0 bg-white border-t p-4 flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button 
            className="flex-1 bg-brand-primary hover:bg-brand-primary/90 text-brand-dark font-bold"
            onClick={() => updateMutation.mutate(formData)}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? "Salvando..." : "Salvar Alterações"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
